import React, { useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { AppTopbar, LAYOUT_MAX_WIDTH, TOPBAR_HEIGHT } from './layout/AppTopbar';
import { useHideTopbarOnScroll } from '../hooks/useHideTopbarOnScroll';
import { SiteFooter } from './SiteFooter';
import { SiteCopyrightBar } from './SiteCopyrightBar';
import { SidebarProvider } from '../contexts/SidebarContext';
import { ChatProvider } from '../contexts/ChatContext';
import { CommunityProvider } from '../contexts/CommunityContext';
import { SolanaWalletProvider } from '../features/wallet/providers/SolanaWalletProvider';
import { WalletAccountProvider } from '../features/wallet/context/WalletAccountProvider';
import { CWY_COLORS } from '../theme/colors';

const LayoutContent: React.FC = () => {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isCanadaEsim = location.pathname.startsWith('/store/canada-esim');
  const isMembership =
    location.pathname === '/membership' ||
    location.pathname.startsWith('/membership/');

  const isPaymentResult = location.pathname === '/payment/result';

  const isFullHeightPage =
    location.pathname === '/chat' ||
    location.pathname === '/dashboard' ||
    location.pathname === '/profile/wallet' ||
    isPaymentResult ||
    /^\/appointments\/[^/]+\/book$/.test(location.pathname) ||
    /^\/community\/[^/]+$/.test(location.pathname) ||
    location.pathname.startsWith('/community/join/');

  const mainScrollRef = useRef<HTMLElement>(null);
  const topbarHidden = useHideTopbarOnScroll(
    mainScrollRef,
    !isPaymentResult && !isFullHeightPage && !isHome,
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        width: '100vw',
        overflow: 'hidden',
        backgroundColor: isMembership ? 'var(--cwy-pageBg, #FFFFFF)' : CWY_COLORS.pageBg,
      }}
    >
      {!isPaymentResult && (
        <Box
          sx={{
            flexShrink: 0,
            /* On mobile: 0 height so hero fills from top; topbar is fixed overlay */
            height: { xs: 0, sm: topbarHidden ? 0 : TOPBAR_HEIGHT },
            overflow: { xs: 'visible', sm: 'hidden' },
            transition: 'height 0.28s ease',
            willChange: 'height',
            bgcolor: { xs: 'transparent', sm: CWY_COLORS.pageBg },
            position: { xs: 'relative', sm: 'static' },
            zIndex: { xs: 1200, sm: 'auto' },
          }}
        >
          <AppTopbar />
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {isFullHeightPage ? (
          <>
            <Box
              component="main"
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  maxWidth: isPaymentResult ? 'none' : LAYOUT_MAX_WIDTH,
                  width: '100%',
                  mx: 'auto',
                  px: isPaymentResult ? 0 : { xs: 1.5, sm: 2, md: 3 },
                  py: isPaymentResult ? 0 : { xs: 1, sm: 1.5 },
                  minHeight: 0,
                  overflow: 'hidden',
                }}
              >
                <Outlet />
              </Box>
            </Box>
            {!isPaymentResult && <SiteCopyrightBar />}
          </>
        ) : (
          <Box
            ref={mainScrollRef}
            component="main"
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              overflowX: isHome || isCanadaEsim ? 'hidden' : undefined,
              backgroundColor: isMembership ? 'transparent' : undefined,
            }}
          >
            <Box
              sx={{
                flex: isCanadaEsim ? '1 0 auto' : 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                maxWidth: isHome || isMembership || isCanadaEsim ? 'none' : LAYOUT_MAX_WIDTH,
                width: '100%',
                mx: 'auto',
                px: isHome || isMembership || isCanadaEsim ? 0 : { xs: 1.5, sm: 2, md: 3 },
                pt: isHome || isMembership || isCanadaEsim ? 0 : { xs: 2, sm: 2.5 },
                pb: 0,
                ...(isHome ? { minWidth: 0, width: '100%', maxWidth: '100%' } : {}),
              }}
            >
              <Outlet />
            </Box>
            {isHome ? <SiteFooter /> : <SiteCopyrightBar />}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const Layout: React.FC = () => {
  return (
    <SidebarProvider>
      <ChatProvider>
          <CommunityProvider>
            <SolanaWalletProvider>
              <WalletAccountProvider>
                <LayoutContent />
              </WalletAccountProvider>
            </SolanaWalletProvider>
          </CommunityProvider>
      </ChatProvider>
    </SidebarProvider>
  );
};
